import { useCallback, useMemo } from "react";

const STALE_THRESHOLD_MS = 7 * 60 * 1000; // 7 minutes

interface StoredValue {
  scorecardId: number;
  startedAt: number;
}

function getStorageKey(asin: string): string {
  return `scorecard:generating:${asin}`;
}

function readEntry(asin: string): StoredValue | null {
  try {
    const raw = localStorage.getItem(getStorageKey(asin));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredValue;
    if (Date.now() - parsed.startedAt > STALE_THRESHOLD_MS) {
      localStorage.removeItem(getStorageKey(asin));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function useScorecardGeneratingState(asin: string) {
  const entry = useMemo(() => readEntry(asin), [asin]);

  const generatingId = entry?.scorecardId ?? undefined;
  const startedAt = entry?.startedAt ?? undefined;

  const setGenerating = useCallback(
    (scorecardId: number) => {
      const value: StoredValue = { scorecardId, startedAt: Date.now() };
      localStorage.setItem(getStorageKey(asin), JSON.stringify(value));
    },
    [asin]
  );

  const clearGenerating = useCallback(() => {
    localStorage.removeItem(getStorageKey(asin));
  }, [asin]);

  return { generatingId, startedAt, setGenerating, clearGenerating };
}
